import { useMemo } from 'react';
import useClassificationLabelHook from '../hooks/useClassificationLabelHook'; // Use default import
import { useSwal } from '../../../../../components/Notifications/swal/SwalCom';
const ClassificationRight = (props) => {
    const {
        classCount,
        setClassCount,
        classes,
        dragOver,
        activeDragClass,
        selectedImage,
        selectedImages,
        setShowDeleteButton,
        showDeleteButton,
        handlers,
        // the upload progress overlay is rendered once, by ClassificationLeft
    } = useClassificationLabelHook();
    const { swalFail, swalConfirm, swalSuccess } = useSwal();

    // Same badge-order map as the unlabeled grid — see ClassificationLeft.
    const selectionOrder = useMemo(() => {
        const order = new Map();
        let n = 1;
        selectedImages?.forEach((id) => order.set(id, n++));
        return order;
    }, [selectedImages]);
    return (
        <div>
            <div className="col-md-12">
                {/* CREATE CLASSES */}
                <div className="input-group m-t-10">
                    <input
                        type="number"
                        name="task-insert"
                        className="form-control"
                        placeholder="Enter number of Classes"
                        id="classCount"
                        min={1}
                        max={40}
                        value={classCount}
                        onChange={(e) => {
                            let value = parseInt(e.target.value, 10);
                            if (isNaN(value)) {
                                setClassCount(1); // default fallback
                            } else {
                                if (value > 40) {
                                    swalFail("Limit reached", "Maximum 40 classes are allowed.");
                                }
                                value = Math.max(1, Math.min(40, value));
                                setClassCount(value);
                            }
                        }}
                    />
                    <div className="input-group-append">
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                const targetCount = parseInt(classCount, 10) || 0;
                                if (targetCount <= 0 || targetCount > 40) return;

                                if (classes.length === 0) {
                                    // No existing classes: initialize fresh
                                    handlers.handleInitializeClasses();
                                    swalSuccess("Classes created!", `${targetCount} class(es) created.`);
                                } else if (targetCount > classes.length) {
                                    // Add new classes: preserve existing data
                                    handlers.handleAddClasses();
                                    const added = targetCount - classes.length;
                                    swalSuccess("Classes added!", `${added} new class(es) added. Previous classes and their images are preserved.`);
                                } else if (targetCount < classes.length) {
                                    // Reduce classes: confirm before removing
                                    const toRemove = classes.length - targetCount;
                                    swalConfirm(
                                        "Reduce classes?",
                                        `${toRemove} class(es) and their images will be removed. This cannot be undone. Continue?`,
                                        (result) => {
                                            if (result?.isConfirmed) {
                                                handlers.handleReduceClasses(targetCount);
                                                swalSuccess("Classes reduced", `Reduced to ${targetCount} class(es).`);
                                            }
                                        }
                                    );
                                } else {
                                    // Same count: nothing to do
                                    swalSuccess("No change", `Already have ${targetCount} class(es).`);
                                }
                            }}
                        >
                            <i className="feather icon-plus"></i> Create Classes
                        </button>
                    </div>
                </div>

                {/* CLASSES LIST */}
                <div className="classes-section">
                    <div className="classes-list">
                        {Array.isArray(classes) ? (
                            classes.map((cls) => (
                                // For each class
                                <div
                                    key={cls.id}
                                    className={`class-block m-t-15 ${dragOver && activeDragClass === cls.id ? 'drag-over' : ''}`}
                                    onDragOver={(e) => handlers.handleDragOver(e, cls.id)}
                                    onDragLeave={handlers.handleDragLeave}
                                    onDrop={(e) => handlers.handleDrop(e, cls.id)}>
                                    <div className="input-group">
                                        <div className="input-group-append">
                                            <label className="btn btn-primary class-label btn-sm">count {cls.images?.length}</label>
                                        </div>
                                        <input
                                            type="text"
                                            value={cls.name}
                                            onChange={(e) => handlers.handleClassNameChange(cls.id, e.target.value)}
                                            className="form-control form-control-sm"
                                        />
                                        <div className="input-group-append">
                                            <label
                                                htmlFor={`class-${cls.id}-file-upload`}
                                                className="btn btn-primary class-label btn-sm"
                                            >
                                                <i className="feather icon-upload"></i>
                                            </label>
                                            <input
                                                id={`class-${cls.id}-file-upload`}
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                onChange={(e) => handlers.handleFileInput(e, cls.id)}
                                                style={{ display: 'none' }}
                                            />
                                        </div>

                                    </div>
                                    <div className="class-images">
                                        {cls.images.map((image) => (
                                            // For each image in the class
                                            <div
                                                key={image.id}
                                                className={`image-item ${selectedImage?.id === image.id ? 'selected' : ''} ${selectedImages?.has(image.id) ? 'multi-selected' : ''}`}
                                                draggable
                                                onDragStart={(e) => handlers.handleImageDragStart(e, image)}
                                                onClick={(e) => handlers.handleImageClick(image, e)}
                                                onContextMenu={(e) => {
                                                    e.preventDefault();
                                                    handlers.toggleImageSelection(image.id, e.shiftKey);
                                                }}
                                                onMouseEnter={() => setShowDeleteButton(image.id)}
                                                onMouseLeave={() => setShowDeleteButton(null)}
                                            >
                                                                       <img
  src={image.url_display ?? image.url}
  alt={image.name || "Image"}
/>
                                                <button
                                                    className={`delete-image-btn ${showDeleteButton === image.id ? 'visible' : ''}`}
                                                    onClick={(e) => handlers.deleteImage(image.id, e)}
                                                >
                                                    ×
                                                </button>
                                                {selectionOrder.has(image.id) && (
                                                    <div className="selection-badge">{selectionOrder.get(image.id)}</div>
                                                )}
                                            </div>
                                        ))}
                                        {cls.images.length === 0 && (
                                            <p className="drop-hint">Drag images here or use the upload button</p>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-classes">
                                <p>Error: Classes is not an array. Please reinitialize classes.</p>
                            </div>
                        )}
                        {Array.isArray(classes) && classes.length === 0 && (
                            <div className='initial-overlay'>
                                <h2>Classes</h2>
                                <div className="no-classes">
                                    <p>Enter the number of classes and click "Create Classes" to get started</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClassificationRight;